import React, { useEffect, useRef, useState } from 'react';
import {
  formatGroupedNumber,
  formatGroupedNumberInput,
  isPartialGroupedNumber,
  parseGroupedNumber,
} from '../../utils/formatters';

export interface DecimalInputProps {
  id?: string;
  label?: React.ReactNode;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  required?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  error?: string;
  className?: string;
  inputClassName?: string;
  allowEmpty?: boolean;
  /** When true, only commits integers on blur (display still allows typing decimals). */
  integer?: boolean;
  /** When true, show thousand separators while typing and on blur. */
  grouped?: boolean;
  /** Fixed decimal places on blur when grouped (omit to keep typed precision). */
  fractionDigits?: number;
}

function formatValue(
  value: number | undefined,
  grouped: boolean,
  fractionDigits: number | undefined,
): string {
  if (value === undefined || Number.isNaN(value)) return '';
  if (!grouped) return String(value);
  if (fractionDigits !== undefined) {
    return formatGroupedNumber(value, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }
  return formatGroupedNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  });
}

function isPartialDecimal(raw: string): boolean {
  return raw === '' || raw === '.' || raw === '-' || raw === '-.';
}

export default function DecimalInput({
  id,
  label,
  value,
  onChange,
  required = false,
  min,
  max,
  placeholder = '0',
  error,
  className = '',
  inputClassName = '',
  allowEmpty = false,
  integer = false,
  grouped = false,
  fractionDigits,
}: DecimalInputProps) {
  const [displayValue, setDisplayValue] = useState(() =>
    formatValue(value, grouped, fractionDigits),
  );
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDisplayValue(formatValue(value, grouped, fractionDigits));
    }
  }, [value, grouped, fractionDigits]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (grouped) {
      if (raw !== '' && !/^-?[\d,\s]*\.?\d*$/.test(raw)) return;
      const formatted = formatGroupedNumberInput(raw);
      setDisplayValue(formatted);
      if (isPartialGroupedNumber(formatted)) return;
      const parsed = parseGroupedNumber(formatted);
      if (parsed === undefined) return;
      const next = integer ? Math.floor(parsed) : parsed;
      if (min !== undefined && next < min) return;
      if (max !== undefined && next > max) return;
      onChange(next);
      return;
    }

    if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
    setDisplayValue(raw);

    if (isPartialDecimal(raw)) return;

    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return;

    const next = integer ? Math.floor(parsed) : parsed;
    if (min !== undefined && next < min) return;
    if (max !== undefined && next > max) return;
    onChange(next);
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = () => {
    focusedRef.current = false;
    const raw = displayValue.trim();

    if (grouped) {
      if (isPartialGroupedNumber(raw) || raw === '') {
        if (allowEmpty) {
          setDisplayValue('');
          onChange(undefined);
        } else {
          const fallback = min ?? 0;
          setDisplayValue(formatValue(fallback, true, fractionDigits));
          onChange(fallback);
        }
        return;
      }

      let next = parseGroupedNumber(raw);
      if (next === undefined) {
        setDisplayValue(formatValue(value, true, fractionDigits));
        return;
      }
      if (integer) next = Math.floor(next);
      if (min !== undefined) next = Math.max(min, next);
      if (max !== undefined) next = Math.min(max, next);
      setDisplayValue(formatValue(next, true, fractionDigits));
      onChange(next);
      return;
    }

    if (isPartialDecimal(raw)) {
      if (allowEmpty) {
        setDisplayValue('');
        onChange(undefined);
      } else {
        const fallback = min ?? 0;
        setDisplayValue(String(fallback));
        onChange(fallback);
      }
      return;
    }

    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) {
      setDisplayValue(formatValue(value, false, undefined));
      return;
    }

    let next = integer ? Math.floor(parsed) : parsed;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    setDisplayValue(String(next));
    onChange(next);
  };

  const input = (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      required={required}
      className={
        inputClassName ||
        `mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${error ? 'border-red-300' : ''}`
      }
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
    />
  );

  if (!label) return input;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700" htmlFor={id}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {input}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
